import { cva } from "class-variance-authority";

// The closed control. Its metrics equal VInput (inputCva / inputContentCva), so a select lines up with an input
// beside it. The trigger button inside fills the pill; the clear button and the chevron sit next to it.
export const modernSelectChromeCva = cva(
  "relative flex items-center rounded-full border transition-[border-color,box-shadow] duration-150",
  {
    variants: {
      triggerStyle: {
        field: "w-full pr-[15px]",
        compact: "inline-flex w-auto max-w-full pr-3",
      },
      size: {
        medium: "h-8",
        large: "h-10",
      },
      // All three take the input background (as before); "dark-gray" only differs in its group headers.
      variant: {
        "is-gray": null,
        "is-white": null,
        "dark-gray": null,
      },
      state: {
        default: "cursor-pointer text-(--input-text)",
        loading: "cursor-progress text-(--input-text)",
        disabled: "cursor-not-allowed bg-gray-50 text-gray-500 dark:bg-dark-850 dark:text-dark-400",
      },
      tone: {
        default: "border-(--input-border) has-[.modern-select-trigger:focus-visible]:border-primary-500 has-[.modern-select-trigger:focus-visible]:ring-[3px] has-[.modern-select-trigger:focus-visible]:ring-primary-500/15",
        open: "border-primary-500 ring-[3px] ring-primary-500/15",
        error: "border-danger-600 has-[.modern-select-trigger:focus-visible]:ring-[3px] has-[.modern-select-trigger:focus-visible]:ring-danger-600/15",
        errorOpen: "border-danger-600 ring-[3px] ring-danger-600/15",
      },
    },
    compoundVariants: [
      { variant: ["is-gray", "is-white", "dark-gray"], state: ["default", "loading"], class: "bg-(--input-background)" },
    ],
    defaultVariants: {
      triggerStyle: "field",
      size: "medium",
      variant: "is-gray",
      state: "default",
      tone: "default",
    },
  },
);

export const modernSelectTriggerCva = cva(
  "modern-select-trigger flex h-full min-w-0 flex-1 items-center gap-2 rounded-full bg-transparent text-left outline-hidden disabled:cursor-not-allowed",
  {
    variants: {
      triggerStyle: {
        field: "text-base sm:text-sm",
        compact: "text-sm",
      },
      hasIconLeft: {
        true: null,
        false: null,
      },
    },
    compoundVariants: [
      { triggerStyle: "field", hasIconLeft: false, class: "pl-5" },
      { triggerStyle: "field", hasIconLeft: true, class: "pl-[15px]" },
      { triggerStyle: "compact", hasIconLeft: false, class: "pl-3.5" },
      { triggerStyle: "compact", hasIconLeft: true, class: "pl-1" },
    ],
    defaultVariants: {
      triggerStyle: "field",
      hasIconLeft: false,
    },
  },
);

export const modernSelectGroupHeaderCva = cva(
  "flex items-center gap-1.5 px-2.5 pt-2 pb-1 text-[11px] font-bold uppercase leading-4 tracking-[0.06em]",
  {
    variants: {
      variant: {
        "is-gray": "text-gray-600 dark:text-gray-400",
        "is-white": "text-gray-600 dark:text-gray-400",
        "dark-gray": "text-theme-light-gray-2 dark:text-theme-dark-gray-2",
      },
    },
    defaultVariants: {
      variant: "is-gray",
    },
  },
);

export const modernSelectIconTileCva = cva("inline-flex shrink-0 items-center justify-center", {
  variants: {
    size: {
      item: "size-7 rounded-lg text-[13px]",
    },
    selected: {
      true: "bg-white text-primary-600 shadow-xs dark:bg-dark-800 dark:text-primary-400",
      false: "bg-gray-100 text-gray-600 dark:bg-dark-700 dark:text-gray-300",
    },
  },
  defaultVariants: {
    size: "item",
    selected: false,
  },
});

// The icon of a compact trigger, tinted so the pill reads as the current context of the page.
export const modernSelectTriggerTileClasses = "inline-flex size-[22px] shrink-0 items-center justify-center rounded-full bg-primary-50 text-[11px] text-primary-600 dark:bg-primary-500/15 dark:text-primary-400";

export const modernSelectOptionCva = cva(
  "item flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1 text-sm select-none",
  {
    variants: {
      twoLine: {
        true: "min-h-11",
        false: "min-h-9",
      },
      state: {
        default: "cursor-pointer text-gray-900 dark:text-gray-100",
        active: "is-hovered cursor-pointer bg-gray-50 text-gray-900 dark:bg-dark-700 dark:text-gray-100",
        selected: "is-active cursor-pointer bg-primary-50 font-medium text-gray-900 dark:bg-primary-500/15 dark:text-white",
        selectedActive: "is-active is-hovered cursor-pointer bg-primary-50 font-medium text-gray-900 ring-1 ring-inset ring-primary-500/30 dark:bg-primary-500/20 dark:text-white",
        disabled: "disabled cursor-not-allowed text-gray-500 dark:text-gray-500",
      },
    },
    defaultVariants: {
      twoLine: false,
      state: "default",
    },
  },
);

// Inside `v-html`, so these classes must stay literal strings for Tailwind to pick them up.
export const modernSelectMarkClasses = "rounded-xs bg-yellow-100 text-inherit dark:bg-yellow-400/25";
